/**
 * Lecture d'un fichier de référentiel au back-office (14/10/2026) : CSV (tout
 * séparateur, UTF-8 ou Windows-1252 — les fichiers INSEE et La Poste sont
 * souvent en Latin-1) ou Excel (.xlsx, plusieurs feuilles). Tout est lu dans
 * le navigateur : seules les colonnes associées partent vers l'API.
 */

export interface Feuille {
  nom: string;
  lignes: string[][];
}

/** Texte d'un fichier : UTF-8 s'il est valide, sinon Windows-1252. BOM retiré. */
export function decoder(octets: ArrayBuffer): string {
  let texte: string;
  try {
    texte = new TextDecoder('utf-8', { fatal: true }).decode(octets);
  } catch {
    texte = new TextDecoder('windows-1252').decode(octets);
  }
  return texte.replace(/^\uFEFF/, '');
}

/** Le séparateur le plus fréquent de la première ligne : « ; », « , » ou tabulation. */
export function separateur(texte: string): string {
  const premiere = texte.split(/\r?\n/, 1)[0] ?? '';
  const comptes = [';', ',', '\t'].map((s) => ({ s, n: premiere.split(s).length - 1 }));
  return comptes.sort((a, b) => b.n - a.n)[0]?.n ? (comptes[0]?.s ?? ';') : ';';
}

/** CSV → lignes de cellules (guillemets doublés, retours à la ligne dans une cellule). */
export function lireCsv(texte: string, sep = separateur(texte)): string[][] {
  const lignes: string[][] = [];
  let ligne: string[] = [];
  let champ = '';
  let guillemets = false;
  for (let i = 0; i < texte.length; i += 1) {
    const c = texte[i];
    if (guillemets) {
      if (c === '"' && texte[i + 1] === '"') {
        champ += '"';
        i += 1;
      } else if (c === '"') guillemets = false;
      else champ += c;
    } else if (c === '"' && champ === '') guillemets = true;
    else if (c === sep) {
      ligne.push(champ);
      champ = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && texte[i + 1] === '\n') i += 1;
      ligne.push(champ);
      if (ligne.some((x) => x.trim() !== '')) lignes.push(ligne);
      ligne = [];
      champ = '';
    } else champ += c;
  }
  ligne.push(champ);
  if (ligne.some((x) => x.trim() !== '')) lignes.push(ligne);
  return lignes;
}

/** Une cellule Excel en texte : entier sans « .0 », date en AAAA-MM-JJ. */
export function texteCellule(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(v);
  return String(v);
}

/**
 * La ligne d'en-tête probable : la première dont la plupart des cellules sont
 * remplies — les fichiers INSEE ouvrent sur des lignes de titre.
 */
export function ligneEnTete(lignes: string[][]): number {
  const largeur = Math.max(0, ...lignes.slice(0, 30).map((l) => l.filter((c) => c.trim()).length));
  const i = lignes.findIndex((l) => l.filter((c) => c.trim()).length >= Math.max(2, Math.ceil(largeur * 0.6)));
  return i < 0 ? 0 : i;
}

/** Clé de comparaison d'un en-tête : minuscules, sans accents ni ponctuation. */
export const cleEnTete = (t: string) =>
  t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Associe automatiquement les colonnes aux champs, d'après les indices de
 * chaque champ (« code postal », « libelle »…). Chaque colonne ne sert qu'une
 * fois ; un champ sans colonne reconnue reste à choisir à la main.
 */
export function associer(
  entetes: string[],
  champs: { cle: string; indices: string[] }[],
): Record<string, number | null> {
  const prises = new Set<number>();
  const resultat: Record<string, number | null> = {};
  const cles = entetes.map(cleEnTete);
  for (const champ of champs) {
    let trouve: number | null = null;
    // D'abord l'en-tête identique à un indice, puis celui qui le contient.
    for (const exact of [true, false]) {
      for (const indice of champ.indices.map(cleEnTete)) {
        const i = cles.findIndex((c, j) => !prises.has(j) && c !== '' && (exact ? c === indice : c.includes(indice)));
        if (i >= 0) {
          trouve = i;
          break;
        }
      }
      if (trouve !== null) break;
    }
    if (trouve !== null) prises.add(trouve);
    resultat[champ.cle] = trouve;
  }
  return resultat;
}

/** Les lignes de données (sous l'en-tête), en objets { champ: valeur } prêts à envoyer. */
export function versLignes(lignes: string[][], enTete: number, association: Record<string, number | null>) {
  return lignes.slice(enTete + 1).map((l) =>
    Object.fromEntries(
      Object.entries(association)
        .filter((e): e is [string, number] => e[1] !== null)
        .map(([champ, i]) => [champ, (l[i] ?? '').trim()]),
    ),
  );
}

/** Lit un fichier choisi : CSV, ou toutes les feuilles d'un classeur Excel. */
export async function lireFichier(fichier: File): Promise<Feuille[]> {
  const nom = fichier.name.toLowerCase();
  if (nom.endsWith('.xlsx')) {
    const { default: lireClasseur } = await import('read-excel-file/browser');
    const feuilles = await lireClasseur(fichier);
    return feuilles.map((f) => ({
      nom: f.sheet,
      lignes: f.data.map((ligne) => ligne.map(texteCellule)).filter((l) => l.some((c) => c.trim() !== '')),
    }));
  }
  if (nom.endsWith('.xls')) {
    throw new Error('Ancien format Excel (.xls) : enregistrez le fichier en .xlsx ou en CSV.');
  }
  return [{ nom: fichier.name, lignes: lireCsv(decoder(await fichier.arrayBuffer())) }];
}
